import type { AllUser } from "../types/Users";

export interface CsvExportData extends Record<string, unknown> {
  name: string;
  email: string;
  subscriptionStatus: string;
  activityStatus: string;
  loginCount: number;
  lastLogin: string;
  signupDate: string;
  organisationName: string;
  organisationType: string;
  emailVerified: boolean;
  phoneNumber: string;
  stripeCustomerLink: string;
}

/**
 * Convert user data to CSV export format
 */
export const prepareUserDataForCsv = (users: AllUser[]): CsvExportData[] => {
  return users.map((user) => ({
    name: user.displayName ?? user.name ?? "No name",
    email: user.email ?? "",
    subscriptionStatus: user.subscriptionStatus ?? "",
    activityStatus: user.activity_status,
    loginCount: user.login_count ?? 0,
    lastLogin: user.last_login_at
      ? formatDateForCsv(user.last_login_at)
      : "Never",
    signupDate: user.signup_date
      ? formatDateForCsv(user.signup_date)
      : user.created_at
        ? formatDateForCsv(user.created_at)
        : "",
    organisationName: user.organisation_name ?? "",
    organisationType: user.organisation_type ?? "",
    emailVerified: user.emailVerified ?? false,
    phoneNumber: user.phoneNumber ?? "",
    stripeCustomerLink: user.customerStripeLink ?? "",
  }));
};

/**
 * Format date for CSV export
 */
const formatDateForCsv = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-NZ", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

/**
 * Convert data array to CSV string
 */
export const convertToCsv = <T extends Record<string, unknown>>(
  data: T[],
): string => {
  if (data.length === 0) return "";

  // Get headers from the first object
  const headers = Object.keys(data[0]);

  // Create CSV header row
  const headerRow = headers.map((header) => `"${header}"`).join(",");

  // Create CSV data rows
  const dataRows = data.map((row) =>
    headers
      .map((header) => {
        const value = row[header];
        // Escape quotes and handle different data types
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      })
      .join(","),
  );

  return [headerRow, ...dataRows].join("\n");
};

/**
 * Download CSV file
 */
export const downloadCsv = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export type definitions for different customer segments
 */
export interface CustomerExportOptions {
  type:
    | "all"
    | "business_customers"
    | "active"
    | "trialing"
    | "inactive"
    | "cancelled"
    | "active_users"
    | "inactive_users"
    | "never_logged_in";
  name: string;
  description: string;
}

export const EXPORT_OPTIONS: CustomerExportOptions[] = [
  {
    type: "all",
    name: "All Users",
    description: "Export all users in the system",
  },
  {
    type: "business_customers",
    name: "All Business Customers",
    description: "All paying and trial customers (excludes never logged in)",
  },
  {
    type: "active",
    name: "Active Subscribers",
    description: "Users with active paid subscriptions",
  },
  {
    type: "trialing",
    name: "Trial Users",
    description: "Users currently on trial",
  },
  {
    type: "active_users",
    name: "Recently Active Users",
    description:
      "Users with recorded in-app usage within the last 7 UTC calendar days",
  },
  {
    type: "inactive_users",
    name: "Inactive Users",
    description: "Users whose last recorded usage is 8–30 days ago",
  },
  {
    type: "never_logged_in",
    name: "Never Logged In",
    description: "Users who have never logged in",
  },
  {
    type: "cancelled",
    name: "Cancelled/Churned",
    description:
      "Users who cancelled, or haven't recorded in-app usage for 30+ days",
  },
];
